import type { DriverFaceImageAssignRequest, PageableContent, UUID } from '@cobliteam/common-types';
import { Environment } from '@cobliteam/react-environments';
import camelcaseKeys from 'camelcase-keys';
import decamelizeKeys from 'decamelize-keys';
import queryString from 'query-string';
import type { Configuration, IRequest } from '../..';
import HttpMethod from '../../request/HttpMethod';
import Requester from '../../request/Requester';
import type { UploadFaceImageResult } from './definitions/APITypes';
import type ImagesMatchReview from './definitions/ImagesMatchReview';
import type { ImagesMatchReviewFilters, ImagesMatchReviewUpdate } from './definitions/ImagesMatchReview';
import type { PathImages, PathImagesFilters } from './definitions/PathImages';

const CDN_BASE_URL = 'https://d5c4nsv15lvpd.cloudfront.net/';

const env = Environment.getInstance().getEnvironment();

export class FaceIdRequester {
  private requester: Requester;
  private apiRequester: Requester;

  private readonly baseUrl: string;

  constructor(configuration: Configuration) {
    this.requester = new Requester(configuration);
    this.apiRequester = new Requester({ host: env.apiHost });
    this.baseUrl = '/face-id';
  }

  public deleteDriverFaceCluster = async (id: UUID): Promise<void> => {
    const requestData: IRequest = {
      endpoint: `${this.baseUrl}/clusters/${id}`,
      method: HttpMethod.DELETE,
    };

    await this.requester.request(requestData);
  };

  public uploadFaceImage = async (blobUrl: string, fileName: string): Promise<UploadFaceImageResult> => {
    const file = await fetch(blobUrl).then((response) => response.blob());
    const formData = new FormData();
    formData.append('file', file, fileName);

    const faceImageResponse = await this.requester.request({
      endpoint: `${this.baseUrl}/images/upload`,
      body: formData,
      method: HttpMethod.POST,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return faceImageResponse.data;
  };

  public assignFaceImages = async (assignments: DriverFaceImageAssignRequest[]): Promise<void> => {
    const parsed: DriverFaceImageAssignRequest[] = assignments.map(({ url, ...assignment }) => {
      const faceImageUrl = url.replace(CDN_BASE_URL, '').replace('_cropped', '').split('?')[0];

      return {
        ...assignment,
        url: faceImageUrl,
      };
    });

    await this.requester.request({
      endpoint: `${this.baseUrl}/images/assign`,
      body: JSON.stringify(decamelizeKeys(parsed)),
      method: HttpMethod.PATCH,
    });
  };

  public getImagesMosaic = async (filters: ImagesMatchReviewFilters): Promise<PageableContent<ImagesMatchReview>> => {
    const searchParams = queryString.stringify(filters);

    const requestData: IRequest = {
      endpoint: `${this.baseUrl}/images/match-review?${searchParams}`,
      method: HttpMethod.GET,
    };

    return this.requester.request(requestData).then((response) => camelcaseKeys(response.data, { deep: true }));
  };

  public getImages = async (vehicleId: UUID, filters: PathImagesFilters): Promise<PageableContent<PathImages>> => {
    const searchParams = queryString.stringify({ ...filters, vehicleId });

    const requestData: IRequest = {
      endpoint: `${this.baseUrl}/images?${searchParams}`,
      method: HttpMethod.GET,
    };

    return this.apiRequester.request(requestData).then((response) => camelcaseKeys(response.data, { deep: true }));
  };

  public updateImagesMatchReview = async (reviews: ImagesMatchReviewUpdate[]): Promise<void> => {
    const parsedReviews = reviews.map(({ url, status }) => ({
      url: url.replace(CDN_BASE_URL, '').split('?')[0],
      status,
    }));

    const requestData: IRequest = {
      endpoint: `${this.baseUrl}/images/match-review`,
      method: HttpMethod.POST,
      body: JSON.stringify(parsedReviews),
    };

    await this.requester.request(requestData);
  };
}

export default FaceIdRequester;
